import { useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  CalendarClock,
  Download,
  PartyPopper,
  Receipt,
  RotateCcw,
  Upload,
} from "lucide-react";
import {
  auDate,
  businessDate,
  fromYmd,
  money,
  type MyContractorInvoiceRow,
  type MyInvoiceIntakeDTO,
} from "@contractorhub/shared";
import { apiDownload, apiUpload, errorMessage } from "../../../lib/api";
import { auInstantDate } from "../../../lib/dates";
import { invoiceFocus, type InvoiceFocus } from "../../../lib/invoice-focus";
import { defaultPeriod, monthOptions } from "../../../lib/invoice-months";
import { myInvoiceIntakeCountQuery, myInvoicesQuery } from "../../../lib/queries";
import { useUploadBatch } from "../../../lib/use-upload-batch";
import { MultiPdfDropZone } from "../../../components/invoices/multi-pdf-drop-zone";
import { MyIntakeReviewModal } from "../../../components/invoices/my-intake-review-modal";
import { InvoiceStatusBadge } from "../../../components/invoices/invoice-badges";
import { Amount } from "../../../components/invoices/invoice-display";
import { InvoiceProgress } from "../../../components/invoices/invoice-progress";
import {
  UploadInvoiceModal,
  type UploadMode,
} from "../../../components/invoices/upload-invoice-modal";
import { useToast } from "../../../components/toast";
import { Alert, Button, Card, EmptyState, PageHeader, cn } from "../../../components/ui";

const LIST = myInvoicesQuery({ status: "ALL" });

/** As the admin detail page: ask while a document is being read, then give up. */
const POLL_FOR_MS = 3 * 60_000;
const POLL_EVERY_MS = 3_000;

export const Route = createFileRoute("/_authed/_contractor/my-invoices")({
  loader: ({ context }) => context.queryClient.ensureQueryData(LIST),
  component: MyInvoicesPage,
});

/**
 * The contractor's invoices (CINV-15): one row per month, the workflow status,
 * and the admin's note when there is one to act on. No verdicts, no confidence,
 * no comparison — those are the admin's.
 */
function MyInvoicesPage() {
  // Which reads we are waiting on, and since when. Read only in the interval
  // callback, which runs after each fetch — never during render.
  const waiting = useRef<{ reads: string; since: number } | null>(null);
  const { data } = useSuspenseQuery({
    ...LIST,
    // Poll while any row is still being read, so "Reading the document…" turns
    // into the invoice number and total without a reload. Bounded: a read that
    // never lands is the admin's to re-run, not a reason to poll forever. A new
    // upload (or a new file) is a new read, and restarts the clock.
    refetchInterval: (query) => {
      const reads = (query.state.data?.rows ?? [])
        .filter((row) => row.processing)
        .map((row) => `${row.id}:${row.fileVersion}`)
        .join(",");
      if (!reads) {
        waiting.current = null;
        return false;
      }
      if (waiting.current?.reads !== reads)
        waiting.current = { reads, since: Date.now() };
      return Date.now() - waiting.current.since > POLL_FOR_MS ? false : POLL_EVERY_MS;
    },
    refetchIntervalInBackground: false,
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<UploadMode | null>(null);
  // Which review is open: the drop just made, or every upload not yet submitted.
  const [reviewing, setReviewing] = useState<"batch" | "pending" | null>(null);
  // Dropped PDFs wait as the contractor's own intakes until each is given its
  // month (CINV-21) — read off the invoice when it can be.
  const batch = useUploadBatch<MyInvoiceIntakeDTO>({
    upload: (file) => apiUpload("POST /api/my/invoice-intakes", { fields: { file } }),
    onUploaded: () =>
      void queryClient.invalidateQueries({ queryKey: ["my", "invoice-intakes"] }),
  });
  const { data: intakes } = useQuery(myInvoiceIntakeCountQuery);
  const unsubmitted = intakes?.pendingCount ?? 0;

  const today = businessDate();
  const months = monthOptions(data.rows, today);
  const focus = invoiceFocus(data.rows, today);

  const openNew = (period?: string) =>
    setMode({
      kind: "new",
      months,
      defaultPeriod:
        period && months.some((m) => m.value === period)
          ? period
          : defaultPeriod(months, today),
    });
  const openReupload = (row: MyContractorInvoiceRow) =>
    setMode({ kind: "reupload", invoiceId: row.id, periodLabel: row.periodLabel });

  const download = useMutation({
    mutationFn: (row: MyContractorInvoiceRow) =>
      apiDownload(
        "GET /api/my/invoices/{id}/file",
        { params: { id: row.id } },
        row.fileName
      ),
    onError: (error) => toast(errorMessage(error), { tone: "error" }),
  });

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Invoices"
        description="One PDF a month. We check it against your approved timesheets and let you know when it's approved and paid."
      />

      <div className="mb-6 space-y-3">
        <MultiPdfDropZone
          label="Your invoice PDFs"
          title="Drop your invoice PDF here, or"
          hint="We read which month it's for — you just check it and submit. Several months at once is fine."
          disabled={months.length === 0}
          onFiles={(files) => {
            // A finished batch is done with: the new drop starts its own.
            if (!batch.busy) batch.clear();
            batch.add(files);
            setReviewing("batch");
          }}
          footer={
            months.length === 0 ? (
              "Every month up to now already has an invoice."
            ) : (
              <>
                Rather pick the month first?{" "}
                <Button variant="link" size="sm" onClick={() => openNew()}>
                  Upload invoice
                </Button>
              </>
            )
          }
        />
        {unsubmitted > 0 && reviewing === null && (
          <Alert
            tone="info"
            action={
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  if (batch.busy) return setReviewing("batch");
                  batch.clear();
                  setReviewing("pending");
                }}
              >
                Review
              </Button>
            }
          >
            {unsubmitted === 1
              ? "1 uploaded invoice isn't submitted yet."
              : `${unsubmitted} uploaded invoices aren't submitted yet.`}
          </Alert>
        )}
      </div>

      <FocusCard
        focus={focus}
        onUpload={openNew}
        onReupload={openReupload}
        canUpload={months.length > 0}
      />

      {data.rows.length === 0 ? (
        <Card className="mt-6">
          <EmptyState
            icon={Receipt}
            title="No invoices yet"
            description="Your invoices will be listed here, each with where it is on the way to being paid."
          />
        </Card>
      ) : (
        <section className="mt-8" aria-labelledby="all-invoices">
          <h2
            id="all-invoices"
            className="mb-3 text-micro font-semibold uppercase tracking-wider text-subtle"
          >
            All invoices · {data.rows.length}
          </h2>
          <ul className="space-y-3">
            {data.rows.map((row) => (
              <InvoiceCard
                key={row.id}
                row={row}
                noteShownAbove={focus.kind === "fix" && focus.row.id === row.id}
                downloading={download.isPending && download.variables?.id === row.id}
                onDownload={() => download.mutate(row)}
                onReupload={() => openReupload(row)}
              />
            ))}
          </ul>
        </section>
      )}

      {reviewing === "batch" && (
        <MyIntakeReviewModal
          months={months}
          source={{
            kind: "batch",
            entries: batch.items.map((item) => ({
              key: item.key,
              fileName: item.file.name,
              state: item.state,
              error: item.error,
              intakeId: item.result?.id ?? null,
            })),
            onRetry: batch.retry,
            onForget: batch.remove,
          }}
          onClose={() => {
            setReviewing(null);
            if (!batch.busy) batch.clear();
          }}
        />
      )}
      {reviewing === "pending" && (
        <MyIntakeReviewModal
          months={months}
          source={{ kind: "pending" }}
          onClose={() => setReviewing(null)}
        />
      )}

      {mode && (
        <UploadInvoiceModal
          open
          mode={mode}
          onClose={() => setMode(null)}
          onUploaded={() => undefined}
        />
      )}
    </div>
  );
}

/** What the page leads with — see `invoiceFocus` for the order. */
function FocusCard({
  focus,
  canUpload,
  onUpload,
  onReupload,
}: {
  focus: InvoiceFocus;
  canUpload: boolean;
  onUpload: (period?: string) => void;
  onReupload: (row: MyContractorInvoiceRow) => void;
}) {
  if (focus.kind === "fix") {
    const { row } = focus;
    return (
      <Card className="overflow-hidden border-warning-soft-border">
        <div className="flex flex-col gap-4 bg-warning-soft/60 p-5 sm:flex-row sm:items-start">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-warning-soft text-warning-on-soft">
            <RotateCcw className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-md font-semibold text-text">
              Your {row.periodLabel} invoice needs a fix
            </h2>
            <p className="mt-0.5 text-sm text-muted">
              Upload a corrected PDF and it goes straight back into review.
            </p>
            {row.reviewNote && (
              <blockquote className="mt-3 rounded-lg border-l-2 border-warning-solid bg-surface px-3 py-2 text-sm text-text">
                {row.reviewNote}
              </blockquote>
            )}
          </div>
          <Button icon={Upload} className="shrink-0" onClick={() => onReupload(row)}>
            Upload corrected invoice
          </Button>
        </div>
      </Card>
    );
  }

  if (focus.kind === "upload") {
    return (
      <Card className="overflow-hidden border-accent-soft-border">
        <div className="flex flex-col gap-4 bg-accent-soft/50 p-5 sm:flex-row sm:items-center">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-accent-soft text-accent-on-soft">
            <CalendarClock className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-md font-semibold text-text">
              Your {focus.label} invoice isn't in yet
            </h2>
            <p className="mt-0.5 text-sm text-muted">
              Upload it once the month's timesheets are approved — we'll check it against
              them for you.
            </p>
          </div>
          <Button
            icon={Upload}
            className="shrink-0"
            disabled={!canUpload}
            onClick={() => onUpload(focus.period)}
          >
            Upload {focus.label.split(" ")[0]} invoice
          </Button>
        </div>
      </Card>
    );
  }

  if (focus.kind === "track") {
    const { row } = focus;
    return (
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-micro font-medium uppercase tracking-wider text-subtle">
              Latest invoice
            </div>
            <h2 className="mt-0.5 text-md font-semibold text-text">
              {row.periodLabel}
              {row.invoiceNumber ? (
                <span className="font-normal text-subtle"> · {row.invoiceNumber}</span>
              ) : null}
            </h2>
            <p className="mt-0.5 text-sm text-muted">
              {row.status === "APPROVED"
                ? "Approved — payment is on its way."
                : row.status === "PART_PAID"
                  ? `Part paid — ${money(row.amountPaid, row.currency)} so far, ${money(row.balance ?? 0, row.currency)} still to come.`
                  : row.processing
                  ? "We're reading the document now."
                  : "With the Simplok team for review."}
            </p>
          </div>
          <Amount
            value={row.total}
            currency={row.currency}
            className="text-2xl font-semibold tracking-tight text-text"
          />
        </div>
        <InvoiceProgress invoice={row} className="mt-5" />
      </Card>
    );
  }

  return (
    <Card className="flex flex-wrap items-center gap-4 p-5">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-success-soft text-success-on-soft">
        <PartyPopper className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <h2 className="text-md font-semibold text-text">You're all caught up</h2>
        <p className="mt-0.5 text-sm text-muted">
          Every invoice is paid. Your {focus.nextLabel} invoice is due once the month
          ends.
        </p>
      </div>
    </Card>
  );
}

function InvoiceCard({
  row,
  noteShownAbove,
  downloading,
  onDownload,
  onReupload,
}: {
  row: MyContractorInvoiceRow;
  /** The focus card already quotes this invoice's note; saying it twice is noise. */
  noteShownAbove: boolean;
  downloading: boolean;
  onDownload: () => void;
  onReupload: () => void;
}) {
  return (
    <li className="rounded-xl border border-line bg-surface p-4 shadow-e1 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <h3 className="text-md font-semibold text-text">{row.periodLabel}</h3>
          <p className="mt-0.5 truncate text-xs text-subtle">
            {row.invoiceNumber ?? (row.processing ? "Reading…" : "No number yet")} ·{" "}
            {row.fileName}
            {row.fileVersion > 1 ? ` · v${row.fileVersion}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Amount
            value={row.total}
            currency={row.currency}
            className="text-lg font-semibold text-text"
          />
          <InvoiceStatusBadge status={row.status} />
        </div>
      </div>

      <InvoiceProgress invoice={row} className="mt-4" />

      {row.payments.length > 0 && <InvoicePayments row={row} />}

      {row.reviewNote && !noteShownAbove && (
        <p
          className={cn(
            "mt-3 rounded-lg px-3 py-2 text-sm",
            row.status === "REJECTED"
              ? "bg-danger-soft text-danger-on-soft"
              : "bg-warning-soft text-warning-on-soft"
          )}
        >
          {row.reviewNote}
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-line pt-3">
        <span className="text-xs text-subtle">
          {row.paidAt
            ? `Paid ${auInstantDate(row.paidAt)}`
            : row.status === "PART_PAID"
              ? `Part paid · ${money(row.balance ?? 0, row.currency)} still to pay`
              : `Uploaded ${auInstantDate(row.submittedAt)}`}
        </span>
        <div className="flex gap-1">
          {row.canReplaceFile && row.status === "CHANGES_REQUESTED" && (
            <Button size="sm" variant="secondary" icon={RotateCcw} onClick={onReupload}>
              Re-upload
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            icon={Download}
            aria-label={`Download the invoice for ${row.periodLabel}`}
            loading={downloading}
            onClick={onDownload}
          >
            Download
          </Button>
        </div>
      </div>
    </li>
  );
}

/**
 * Each payment made against the invoice, oldest first (CINV-23): the day, the
 * amount and the reference to look for on a bank statement. A short close says
 * how much was not paid and why — never left for the contractor to work out.
 */
function InvoicePayments({ row }: { row: MyContractorInvoiceRow }) {
  return (
    <div className="mt-3 rounded-lg bg-surface-sunken px-3 py-2.5 text-sm">
      <div className="text-micro font-medium uppercase tracking-wider text-subtle">
        {row.payments.length === 1 ? "Payment" : "Payments"}
      </div>
      <ul className="mt-1 space-y-0.5">
        {row.payments.map((payment) => (
          <li key={payment.id} className="flex flex-wrap justify-between gap-x-3 text-text">
            <span>
              {auDate(fromYmd(payment.paidOn))}
              {payment.reference ? <span className="text-subtle"> · ref {payment.reference}</span> : null}
            </span>
            <span className="font-medium tabular-nums">{money(payment.amount, row.currency)}</span>
          </li>
        ))}
      </ul>
      {row.shortClose ? (
        <p className="mt-2 text-xs text-warning-on-soft">
          Closed as paid with {money(row.shortClose.shortfall, row.currency)} not paid: {row.shortClose.reason}
        </p>
      ) : row.balance !== null && row.balance > 0 ? (
        <p className="mt-2 text-xs text-muted">
          {money(row.balance, row.currency)} of {money(row.total ?? 0, row.currency)} still to pay.
        </p>
      ) : null}
    </div>
  );
}
